import { type VirtualItem } from '@tanstack/react-virtual';
import * as React from 'react';

import { cn } from '../utils';

export interface DataGridHeaderCellProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Virtual column object containing position and size info */
  virtualColumn?: VirtualItem;
  /** Absolute left position (used when virtualColumn not provided) */
  left?: number;
  /** Cell width (used when virtualColumn not provided) */
  width?: number;
  /** Cell height */
  height: number;
  /** Whether this is a sticky corner cell */
  isSticky?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Additional styles */
  style?: React.CSSProperties;
  /** Offset to subtract from virtualColumn.start for proper positioning */
  leftOffset?: number;
}

/**
 * Individual header cell for virtualized data grid.
 * Can be either sticky (corner) or absolutely positioned (virtualized).
 */
export const DataGridHeaderCell = React.forwardRef<
  HTMLDivElement,
  DataGridHeaderCellProps
>(
  (
    {
      virtualColumn,
      left,
      width,
      height,
      isSticky = false,
      className,
      style,
      children,
      leftOffset = 0,
      ...props
    },
    ref,
  ) => {
    // Calculate position and size from virtualColumn or fallback to direct props
    const calculatedLeft = virtualColumn
      ? virtualColumn.start - leftOffset
      : (left ?? 0);
    const calculatedWidth = virtualColumn ? virtualColumn.size : (width ?? 0);

    return (
      <div
        role="table-header"
        ref={ref}
        className={cn(
          'flex items-center bg-grey-10 text-xs font-medium text-left px-6',
          isSticky && 'sticky left-0 z-[4]',
          !isSticky && 'absolute',
          className,
        )}
        style={{
          ...(isSticky ? { left: 0 } : { left: calculatedLeft }),
          width: calculatedWidth,
          height,
          ...style,
        }}
        {...props}
      >
        {children}
      </div>
    );
  },
);

DataGridHeaderCell.displayName = 'DataGridHeaderCell';
